import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Confetti from 'react-confetti';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, Crown } from 'lucide-react';

const SubscriptionSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showConfetti, setShowConfetti] = useState(true);

  const plan = searchParams.get('plan') || '';
  const reference = searchParams.get('reference') || '';

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const planLabel = plan
    ? plan.charAt(0).toUpperCase() + plan.slice(1)
    : t('subscription.plan', 'Plan');

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/5 flex items-center justify-center p-4">
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
        />
      )}

      <Card className="w-full max-w-2xl shadow-2xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <CheckCircle className="h-24 w-24 text-green-500 animate-pulse" />
              <Crown className="h-12 w-12 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
          <CardTitle className="text-3xl mb-2">
            {t('subscription.successTitle')}
          </CardTitle>
          <p className="text-muted-foreground text-lg">
            {t('subscription.successSubtitle')}
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Plan activated */}
          <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">
              {t('subscription.activatedPlan')}
            </p>
            <p className="text-4xl font-bold text-green-600 dark:text-green-400">
              {planLabel}
            </p>
          </div>

          {/* Transaction Reference */}
          {reference && reference !== 'native_purchase' && (
            <div className="text-center text-sm text-muted-foreground">
              <p className="mb-1">{t('credits.purchase.transactionId')}</p>
              <code className="bg-muted px-3 py-1 rounded">
                {reference}
              </code>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={() => navigate('/subscription')}
              className="flex-1"
              size="lg"
            >
              {t('subscription.viewMyPlan')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/feed')}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              {t('credits.purchase.backToFeed')}
            </Button>
          </div>

          {/* Receipt Info */}
          <div className="text-center text-xs text-muted-foreground pt-4 border-t">
            <p>{t('credits.purchase.receiptSent')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
